/**
 * Enhanced Dummy Data Generator for IAR HRMS
 * Comprehensive dummy data for testing and development
 */

// Company Data
export const companies = [
  {
    _id: '1',
    name: 'TechNova Solutions',
    legalName: 'TechNova Solutions Inc.',
    code: 'TNS',
    address: {
      street: '123 Innovation Drive',
      city: 'San Francisco',
      state: 'California',
      country: 'United States',
      zipCode: '94105'
    },
    phone: '(415) 555-0123',
    email: 'info@technova.com',
    website: 'https://technova.com',
    countryCode: 'US',
    currency: 'USD'
  },
  {
    _id: '2',
    name: 'GlobalTech Industries',
    legalName: 'GlobalTech Industries Ltd.',
    code: 'GTI',
    address: {
      street: '456 Business Avenue',
      city: 'New York',
      state: 'New York',
      country: 'United States',
      zipCode: '10001'
    },
    phone: '(212) 555-0456',
    email: 'contact@globaltech.com',
    website: 'https://globaltech.com',
    countryCode: 'US',
    currency: 'USD'
  },
  {
    _id: '3',
    name: 'InnovateLabs',
    legalName: 'InnovateLabs Corporation',
    code: 'ILC',
    address: {
      street: '789 Research Blvd',
      city: 'Austin',
      state: 'Texas',
      country: 'United States',
      zipCode: '78701'
    },
    phone: '(512) 555-0789',
    email: 'hello@innovatelabs.com',
    website: 'https://innovatelabs.com',
    countryCode: 'US',
    currency: 'USD'
  }
];

const departments = [
  'Engineering',
  'Marketing',
  'Sales',
  'Human Resources',
  'Finance',
  'Operations',
  'Customer Support',
  'Product Management',
  'Design',
  'Quality Assurance',
  'DevOps',
  'Data Science',
  'Legal',
  'Administration'
];

const positions = [
  'Software Engineer',
  'Senior Software Engineer',
  'Principal Engineer',
  'Engineering Manager',
  'Product Manager',
  'Senior Product Manager',
  'Marketing Manager',
  'Digital Marketing Specialist',
  'Sales Representative',
  'Senior Sales Executive',
  'Account Manager',
  'HR Specialist',
  'HR Manager',
  'Financial Analyst',
  'Senior Financial Analyst',
  'Operations Manager',
  'Customer Support Lead',
  'UX Designer',
  'Senior UX Designer',
  'UI Developer',
  'QA Engineer',
  'Senior QA Engineer',
  'DevOps Engineer',
  'Data Analyst',
  'Data Scientist',
  'Business Analyst',
  'Technical Lead',
  'VP of Engineering',
  'Chief Technology Officer',
  'Chief Executive Officer'
];

const firstNames = [
  'John', 'Jane', 'Michael', 'Sarah', 'David', 'Emma', 'James', 'Lisa',
  'Robert', 'Maria', 'William', 'Jennifer', 'Richard', 'Patricia', 'Charles',
  'Linda', 'Daniel', 'Barbara', 'Matthew', 'Susan', 'Anthony', 'Margaret',
  'Christopher', 'Dorothy', 'Joseph', 'Helen', 'Andrew', 'Nancy', 'Joshua', 'Betty'
];

const lastNames = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis',
  'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson',
  'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin', 'Lee', 'Perez', 'Thompson',
  'White', 'Harris', 'Sanchez', 'Clark', 'Ramirez', 'Lewis', 'Robinson'
];

const cities = [
  'New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'Philadelphia',
  'San Antonio', 'San Diego', 'Dallas', 'San Jose', 'Austin', 'Jacksonville',
  'Fort Worth', 'Columbus', 'Charlotte', 'San Francisco', 'Indianapolis',
  'Seattle', 'Denver', 'Boston', 'El Paso', 'Nashville', 'Detroit', 'Oklahoma City'
];

const states = [
  'California', 'Texas', 'Florida', 'New York', 'Pennsylvania', 'Illinois',
  'Ohio', 'Georgia', 'North Carolina', 'Michigan', 'New Jersey', 'Virginia',
  'Washington', 'Arizona', 'Massachusetts', 'Tennessee', 'Indiana', 'Missouri',
  'Maryland', 'Wisconsin', 'Colorado', 'Minnesota', 'South Carolina', 'Alabama'
];

const countries = ['United States', 'Canada', 'United Kingdom', 'Australia', 'Germany'];

const employmentTypes = ['full-time', 'part-time', 'contract', 'internship'];

const statuses = ['active', 'inactive', 'on-leave'];

const managers = [
  'Alice Johnson', 'Bob Wilson', 'Carol Davis', 'David Brown', 'Emma Taylor',
  'Frank Miller', 'Grace Lee', 'Henry Clark', 'Ivy Rodriguez', 'Jack Martinez'
];

const skills = [
  'JavaScript', 'Python', 'React', 'Node.js', 'SQL', 'AWS', 'Docker',
  'Kubernetes', 'Java', 'C#', 'PHP', 'Ruby', 'Go', 'TypeScript', 'Vue.js',
  'Angular', 'MongoDB', 'PostgreSQL', 'Redis', 'GraphQL', 'REST APIs',
  'Git', 'CI/CD', 'Agile', 'Scrum', 'Project Management', 'UI/UX Design',
  'Figma', 'Adobe Creative Suite', 'Data Analysis', 'Machine Learning'
];

const qualifications = [
  'Bachelor of Science in Computer Science',
  'Master of Business Administration',
  'Bachelor of Arts in Marketing',
  'Master of Science in Engineering',
  'Bachelor of Science in Finance',
  'Associate Degree in Business',
  'PhD in Computer Science',
  'Bachelor of Fine Arts in Design',
  'Master of Science in Data Science',
  'Certificate in Project Management'
];

const certifications = [
  'AWS Certified Solutions Architect',
  'Google Cloud Professional Cloud Architect',
  'Certified Scrum Master',
  'Project Management Professional (PMP)',
  'Cisco Certified Network Associate',
  'Microsoft Azure Solutions Architect',
  'Certified Information Systems Security Professional (CISSP)',
  'Six Sigma Green Belt',
  'ITIL Foundation',
  'CompTIA Security+'
];

// Generate random date between start and end dates
const randomDate = (start, end) => {
  return new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
};

// Generate random phone number
const randomPhone = () => {
  const areaCode = Math.floor(Math.random() * 900) + 100;
  const exchange = Math.floor(Math.random() * 900) + 100;
  const number = Math.floor(Math.random() * 9000) + 1000;
  return `(${areaCode}) ${exchange}-${number}`;
};

// Generate random salary based on position
const randomSalary = (position) => {
  const baseSalaries = {
    'Software Engineer': 75000,
    'Senior Software Engineer': 110000,
    'Product Manager': 95000,
    'Marketing Manager': 80000,
    'Sales Representative': 55000,
    'HR Specialist': 65000,
    'Financial Analyst': 70000,
    'Operations Manager': 85000,
    'Customer Support Lead': 60000,
    'UX Designer': 75000,
    'QA Engineer': 70000,
    'DevOps Engineer': 95000,
    'Data Analyst': 65000,
    'Business Analyst': 70000,
    'Technical Lead': 120000
  };

  const base = baseSalaries[position] || 60000;
  const variation = (Math.random() - 0.5) * 0.4; // ±20% variation
  return Math.round(base * (1 + variation));
};

// Generate random employee data
const generateEmployee = (id, companyCode = 'TNS') => {
  const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
  const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
  const department = departments[Math.floor(Math.random() * departments.length)];
  const position = positions[Math.floor(Math.random() * positions.length)];
  const hireDate = randomDate(new Date(2020, 0, 1), new Date());
  const birthDate = randomDate(new Date(1980, 0, 1), new Date(2000, 11, 31));

  // Generate employee ID using company code
  const employeeId = `${companyCode}${String(id).padStart(4, '0')}`;

  // Generate random skills (3-8 skills)
  const employeeSkills = [];
  const numSkills = Math.floor(Math.random() * 6) + 3;
  const shuffledSkills = [...skills].sort(() => 0.5 - Math.random());
  for (let i = 0; i < numSkills; i++) {
    employeeSkills.push(shuffledSkills[i]);
  }

  // Generate random qualifications (1-3)
  const employeeQualifications = [];
  const numQualifications = Math.floor(Math.random() * 3) + 1;
  const shuffledQualifications = [...qualifications].sort(() => 0.5 - Math.random());
  for (let i = 0; i < numQualifications; i++) {
    employeeQualifications.push(shuffledQualifications[i]);
  }

  // Generate random certifications (0-3)
  const employeeCertifications = [];
  const numCertifications = Math.floor(Math.random() * 4);
  const shuffledCertifications = [...certifications].sort(() => 0.5 - Math.random());
  for (let i = 0; i < numCertifications; i++) {
    employeeCertifications.push(shuffledCertifications[i]);
  }

  return {
    _id: id.toString(),
    employeeId,
    firstName,
    lastName,
    email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@${companyCode.toLowerCase()}.com`,
    phone: randomPhone(),
    dateOfBirth: birthDate.toISOString().split('T')[0],
    gender: Math.random() > 0.5 ? 'male' : 'female',
    maritalStatus: Math.random() > 0.6 ? 'single' : 'married',

    // Employment Information
    department,
    designation: position, // Map position to designation
    employmentType: employmentTypes[Math.floor(Math.random() * employmentTypes.length)],
    manager: managers[Math.floor(Math.random() * managers.length)],
    workLocation: cities[Math.floor(Math.random() * cities.length)],
    hireDate: hireDate.toISOString().split('T')[0],
    joiningDate: hireDate.toISOString().split('T')[0],
    probationPeriod: '3',
    status: statuses[Math.floor(Math.random() * statuses.length)],

    // Compensation
    salary: randomSalary(position),
    hourlyRate: position.includes('Engineer') || position.includes('Developer') ? Math.round(randomSalary(position) / 2080 * 1.25) : 0,
    payFrequency: 'monthly',
    bankName: 'First National Bank',
    accountNumber: Math.floor(Math.random() * 9000000000) + 1000000000,
    ifscCode: 'FNBO0001',

    // Tax Information
    pan: `${firstName.charAt(0)}${lastName.charAt(0)}${lastName.charAt(1)}${lastName.charAt(2)}${Math.floor(Math.random() * 9000) + 1000}${firstName.charAt(0)}`.toUpperCase(),
    taxRegime: Math.random() > 0.5 ? 'old' : 'new',
    pfNumber: `PF${companyCode}${String(id).padStart(6, '0')}`,
    esiNumber: `ESI${companyCode}${String(id).padStart(8, '0')}`,
    uanNumber: `UAN${String(Math.floor(Math.random() * 900000000000) + 100000000000)}`,

    // TDS Declaration
    tdsDeclaration: {
      isDeclared: Math.random() > 0.3, // 70% chance of having TDS declaration
      declarationType: ['no_tds', 'lower_tds', 'higher_tds'][Math.floor(Math.random() * 3)],
      reason: ['no_income', 'below_threshold', 'other'][Math.floor(Math.random() * 3)],
      supportingDocuments: [],
      remarks: Math.random() > 0.5 ? 'Employee has provided necessary documentation for TDS declaration.' : ''
    },

    // Address Information
    address: {
      street: `${Math.floor(Math.random() * 9999) + 1} ${['Main St', 'Oak Ave', 'Elm St', 'Maple Dr', 'Cedar Ln', 'Pine St', 'Washington Ave', 'Park Rd', 'Hill St', 'Valley Dr'][Math.floor(Math.random() * 10)]}`,
      city: cities[Math.floor(Math.random() * cities.length)],
      state: states[Math.floor(Math.random() * states.length)],
      zipCode: String(Math.floor(Math.random() * 90000) + 10000),
      country: countries[Math.floor(Math.random() * countries.length)]
    },

    // Emergency Contact
    emergencyContact: {
      name: `${firstNames[Math.floor(Math.random() * firstNames.length)]} ${lastNames[Math.floor(Math.random() * lastNames.length)]}`,
      relationship: ['Spouse', 'Parent', 'Sibling', 'Child', 'Friend'][Math.floor(Math.random() * 5)],
      phone: randomPhone(),
      email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}.emergency@contact.com`
    },

    // Additional Details
    skills: employeeSkills,
    qualifications: employeeQualifications,
    certifications: employeeCertifications,
    experience: `${Math.floor(Math.random() * 15) + 1} years`,
    previousEmployer: ['TechCorp', 'Innovate Solutions', 'Global Systems', 'NextGen Inc', 'FutureWorks', 'DataTech', 'CloudNine', 'AgileSoft'][Math.floor(Math.random() * 8)],
    references: [
      {
        name: 'Previous Manager',
        company: 'Previous Employer',
        phone: randomPhone(),
        email: 'manager@previous.com'
      }
    ],

    // Profile Image (placeholder)
    photo: null, // Will use initials-based avatar

    // Company association
    companyId: companies.find(c => c.code === companyCode)?._id || '1',

    // Timestamps
    createdAt: hireDate.toISOString(),
    updatedAt: new Date().toISOString()
  };
};

// Generate multiple employees distributed across companies
export const generateDummyEmployees = (count = 50) => {
  const employees = [];
  let employeeId = 1;

  // Distribute employees across companies
  companies.forEach(company => {
    const numEmployees = Math.floor(Math.random() * 20) + 10; // 10-30 employees per company

    for (let i = 0; i < numEmployees; i++) {
      employees.push(generateEmployee(employeeId++, company.code));
    }
  });

  return employees;
};

// Generate stats for dashboard
export const generateDummyStats = (employees) => {
  const totalEmployees = employees.length;
  const activeEmployees = employees.filter(emp => emp.status === 'active').length;
  const inactiveEmployees = employees.filter(emp => emp.status === 'inactive').length;
  const onLeaveEmployees = employees.filter(emp => emp.status === 'on-leave').length;

  const newHiresThisMonth = employees.filter(emp => {
    const hireDate = new Date(emp.hireDate);
    const now = new Date();
    return hireDate.getMonth() === now.getMonth() && hireDate.getFullYear() === now.getFullYear();
  }).length;

  const totalSalary = employees.reduce((sum, emp) => sum + (emp.salary || 0), 0);
  const averageSalary = totalEmployees > 0 ? Math.round(totalSalary / totalEmployees) : 0;

  // Department distribution
  const departmentStats = {};
  employees.forEach(emp => {
    departmentStats[emp.department] = (departmentStats[emp.department] || 0) + 1;
  });

  // Company distribution
  const companyStats = {};
  employees.forEach(emp => {
    const company = companies.find(c => c._id === emp.companyId);
    const companyName = company ? company.name : 'Unknown';
    companyStats[companyName] = (companyStats[companyName] || 0) + 1;
  });

  return {
    totalEmployees,
    activeEmployees,
    inactiveEmployees,
    onLeaveEmployees,
    newHiresThisMonth,
    totalDepartments: departments.length,
    averageSalary,
    departmentStats,
    companyStats
  };
};

// Generate dummy departments
export const generateDummyDepartments = () => {
  return departments.map((dept, index) => ({
    _id: (index + 1).toString(),
    name: dept,
    code: dept.toUpperCase().replace(/\s+/g, '_').substring(0, 10),
    description: `${dept} department handles all related operations and activities.`,
    head: managers[Math.floor(Math.random() * managers.length)],
    budget: Math.floor(Math.random() * 500000) + 100000,
    employeeCount: Math.floor(Math.random() * 20) + 5,
    status: 'active',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }));
};

// Generate dummy designations
export const generateDummyDesignations = () => {
  return positions.map((pos, index) => ({
    _id: (index + 1).toString(),
    title: pos,
    code: pos.toUpperCase().replace(/\s+/g, '_').substring(0, 10),
    department: departments[Math.floor(Math.random() * departments.length)],
    level: ['Entry', 'Mid', 'Senior', 'Lead', 'Principal', 'Executive'][Math.floor(Math.random() * 6)],
    minSalary: randomSalary(pos) * 0.8,
    maxSalary: randomSalary(pos) * 1.5,
    description: `${pos} role responsible for performing related duties and responsibilities.`,
    requirements: ['Bachelor\'s degree', '2+ years experience', 'Technical skills', 'Communication skills'].slice(0, Math.floor(Math.random() * 4) + 1),
    status: 'active',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }));
};

// Generate dummy leave types
export const generateDummyLeaveTypes = () => {
  const leaveTypes = [
    { name: 'Annual Leave', code: 'AL', daysPerYear: 25, carryForward: 5, description: 'Regular annual vacation leave' },
    { name: 'Sick Leave', code: 'SL', daysPerYear: 10, carryForward: 0, description: 'Medical leave for illness' },
    { name: 'Personal Leave', code: 'PL', daysPerYear: 5, carryForward: 0, description: 'Personal time off' },
    { name: 'Maternity Leave', code: 'ML', daysPerYear: 90, carryForward: 0, description: 'Maternity leave for new mothers' },
    { name: 'Paternity Leave', code: 'PTL', daysPerYear: 14, carryForward: 0, description: 'Paternity leave for new fathers' },
    { name: 'Emergency Leave', code: 'EL', daysPerYear: 3, carryForward: 0, description: 'Emergency situations' }
  ];

  return leaveTypes.map((leave, index) => ({
    _id: (index + 1).toString(),
    ...leave,
    status: 'active',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }));
};

// Generate dummy attendance records
export const generateDummyAttendance = (employees, days = 30) => {
  const attendance = [];
  const now = new Date();

  employees.forEach(employee => {
    for (let i = 0; i < days; i++) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);

      // Skip weekends for some employees
      if (date.getDay() === 0 || date.getDay() === 6) {
        if (Math.random() > 0.3) continue; // 70% chance of no weekend work
      }

      const checkIn = new Date(date);
      checkIn.setHours(8 + Math.floor(Math.random() * 2), Math.floor(Math.random() * 60));

      const checkOut = new Date(date);
      checkOut.setHours(16 + Math.floor(Math.random() * 3), Math.floor(Math.random() * 60));

      // Random late arrival or early departure
      if (Math.random() > 0.8) {
        checkIn.setHours(checkIn.getHours() + Math.floor(Math.random() * 3));
      }

      attendance.push({
        _id: `${employee._id}_${date.toISOString().split('T')[0]}`,
        employeeId: employee._id,
        date: date.toISOString().split('T')[0],
        checkIn: checkIn.toISOString(),
        checkOut: checkOut.toISOString(),
        status: Math.random() > 0.95 ? 'absent' : 'present',
        hoursWorked: Math.round((checkOut - checkIn) / (1000 * 60 * 60) * 100) / 100,
        overtime: Math.random() > 0.9 ? Math.floor(Math.random() * 3) + 1 : 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
  });

  return attendance;
};

// Generate comprehensive dummy data set
export const generateCompleteDummyData = () => {
  const employees = generateDummyEmployees();
  const departments = generateDummyDepartments();
  const designations = generateDummyDesignations();
  const leaveTypes = generateDummyLeaveTypes();
  const attendance = generateDummyAttendance(employees, 30);
  const stats = generateDummyStats(employees);

  return {
    companies,
    employees,
    departments,
    designations,
    leaveTypes,
    attendance,
    stats
  };
};
